import type { PriceList } from '../types'
import { apiFetch, getAuthHeader, handleUnauthorized } from './core'

export async function getPriceLists(): Promise<PriceList[]> {
  return apiFetch<PriceList[]>('/api/pricelists')
}

export async function uploadPriceList(file: File): Promise<PriceList> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch('/api/pricelists', {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export function uploadPriceListWithProgress(
  file: File,
  onProgress: (pct: number) => void,
): Promise<PriceList> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    form.append('file', file)

    xhr.open('POST', '/api/pricelists')
    xhr.setRequestHeader('Authorization', getAuthHeader())
    xhr.timeout = 600_000 // 10 minuten — grote bestanden kunnen lang duren

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }

    xhr.onload = () => {
      if (xhr.status === 401) { localStorage.removeItem('token'); window.location.reload(); return }
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)) }
        catch { reject(new Error('Ongeldig antwoord van server')) }
      } else {
        try {
          const msg = JSON.parse(xhr.responseText).detail
          reject(new Error(msg || `Server fout (${xhr.status})`))
        } catch {
          reject(new Error(`Server fout (${xhr.status})${xhr.statusText ? ` — ${xhr.statusText}` : ''}`))
        }
      }
    }

    xhr.ontimeout = () => reject(new Error('Upload timeout na 10 minuten — probeer een kleiner bestand'))
    xhr.onerror = () => reject(new Error(
      'Upload mislukt — de verbinding werd verbroken. Grote bestanden (>50 MB) kunnen problemen geven via de huidige proxy-configuratie.'
    ))
    xhr.send(form)
  })
}

export async function deletePriceList(id: number): Promise<void> {
  return apiFetch<void>(`/api/pricelists/${id}`, { method: 'DELETE' })
}
